<template>
    <div>
        <span>
            - Sale Ends In: 
            <span v-if="isStarted && !isEnded">
                <span>{{ formattedDays }}</span><span>d</span> <span>{{ formattedHours }}</span><span>h</span> <span>{{ formattedMinutes }}</span><span>m</span> <span class="hidden">{{ formattedSeconds }}</span>
            </span>
            <span v-else-if="!isStarted">Coming Soon</span>
            <span v-else>00:00:00:00</span>
        </span>
    </div>
</template>

<script>
/*
* Countdown Timer Vue component
* ---
* Displays a countdown timer with days, hours, minutes, and seconds
* Takes a start date, start time, and duration in hours
* Shows "Coming Soon" before start time and "00:00:00:00" after end time
* 
* Example usage:
*
* <countdown-timer
*   start-date="2025-01-15"
*   start-time="14:30"
*   :hours="72"
* ></countdown-timer>
*/

export default {
    name: 'CountdownTimer',
    components: {
        // No child components needed
    },

    props: {
        startDate: {
            type: String,
            required: true,
            validator: value => /^\d{4}-\d{2}-\d{2}$/.test(value)
        },
        startTime: {
            type: String,
            required: true,
            default: '00:00',
            validator: value => /^\d{2}:\d{2}$/.test(value)
        },
        hours: {
            type: Number,
            required: true,
            default: 72,
            validator: value => value > 0
        }
    },

    data() {
        return {
            now: new Date(),
            timer: null
        }
    },

    computed: {
        startDateTime() {
            return new Date(`${this.startDate}T${this.startTime}`);
        },

        endDateTime() {
            return new Date(this.startDateTime.getTime() + (this.hours * 60 * 60 * 1000));
        },

        isStarted() {
            return this.now >= this.startDateTime;
        },

        isEnded() {
            return this.now >= this.endDateTime;
        },

        timeRemaining() {
            if (!this.isStarted || this.isEnded) return 0;
            return this.endDateTime - this.now;
        },

        days() {
            return Math.floor(this.timeRemaining / (1000 * 60 * 60 * 24));
        },

        remainingHours() {
            return Math.floor((this.timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        },

        minutes() {
            return Math.floor((this.timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        },

        seconds() {
            return Math.floor((this.timeRemaining % (1000 * 60)) / 1000);
        },

        formattedDays() {
            return String(this.days).padStart(2, '0');
        },

        formattedHours() {
            return String(this.remainingHours).padStart(2, '0');
        },

        formattedMinutes() {
            return String(this.minutes).padStart(2, '0');
        },

        formattedSeconds() {
            return String(this.seconds).padStart(2, '0');
        }
    },

    watch: {
        isEnded(newValue) {
            if (newValue && this.timer) {
                this.stopTimer();
            }
        }
    },

    created() {
        // Validate initial date/time format
        if (!this.startDateTime || isNaN(this.startDateTime)) {
            console.error('Invalid start date/time format');
        }
    },

    mounted() {
        this.startTimer();
    },

    beforeDestroy() {
        this.stopTimer();
    },

    methods: {
        startTimer() {
            this.updateTime();
            this.timer = setInterval(this.updateTime, 1000);
        },

        stopTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        },

        updateTime() {
            this.now = new Date();
        }
    }
}
</script>